/**
 * What is in stock, a basket, and placing the order (PT-06, V5, docs/15).
 *
 * Two steps on one screen: choose quantities from the plants in stock, then
 * review and place. The basket and its order id live here until the order is
 * placed, so a dropped connection and a second press of Place are the same
 * order - the id is the server's idempotency key. A basket changed after a
 * failed Place is a different order, and `placing.ts` decides whether it can
 * go, and under which id.
 *
 * The page shows no stock figure (V5). A plant is listed or it is not, and a
 * customer may ask for any quantity: the nursery decides what to supply when
 * it confirms. Review reads the list again, and a plant that has gone since it
 * went into the basket stays in the order, marked, for the nursery to decide.
 *
 * A thousand plants is a normal list, on a phone. So the list is indexed once
 * per read (`catalogue.ts`), the search box filters that index, rows are
 * memoised so typing a quantity redraws one row, and the page draws the first
 * fifty matches and says so, with a button for more, rather than building a
 * thousand rows nobody scrolls to.
 *
 * Prices are this customer's, excluding VAT unless their tier says otherwise,
 * and are what they saw rather than what they will pay: the nursery prices the
 * order when it confirms it.
 */

import { type Cents, formatZAR, today } from '@smf/core';
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  type Basket,
  EMPTY_BASKET,
  type BasketLine,
  checkBasket,
  placeRequest,
  withQuantity,
  without,
  withoutBlanks,
} from './basket.js';
import { catalogueOf, matching } from './catalogue.js';
import { type Attempt, type Settled, placeOrder } from './placing.js';
import type {
  Availability,
  ListedItem,
  Me,
  PortalApi,
  PortalError,
} from './portal-api.js';

/** How many rows the list draws at a time. */
const PAGE = 50;

const GONE = 'May no longer be in stock - the nursery will confirm';

function newOrderId(): string {
  return crypto.randomUUID();
}

function money(value: Cents): string {
  return formatZAR(value);
}

function plants(count: number): string {
  return `${String(count)} plant${count === 1 ? '' : 's'}`;
}

/** Size, variety, category and code, as one quiet line under the name. */
function detailOf(item: ListedItem): string {
  return [item.sizeGrade, item.variety, item.category, item.sku]
    .filter((part) => part !== undefined && part !== '')
    .join(' · ');
}

function perUnit(item: ListedItem): string {
  return item.unit === undefined || item.unit === 'each' ? 'each' : `per ${item.unit}`;
}

function describeRead(error: PortalError): string {
  switch (error.kind) {
    case 'no-access':
      return 'This login no longer has access to ordering. Ask the nursery.';
    case 'not-found':
    case 'id-used':
    case 'refused':
    case 'failed':
      return 'We could not reach the nursery.';
  }
}

function describePlace(error: PortalError): string {
  switch (error.kind) {
    case 'no-access':
      return 'This login no longer has access to ordering. Ask the nursery.';
    case 'refused':
      return `The nursery could not take that: ${error.message}`;
    case 'id-used':
      // `placeOrder` reads this as the earlier order and never hands it on;
      // the words are here so that a new path cannot show it bare.
      return 'An earlier attempt at this order reached the nursery. See My orders.';
    case 'not-found':
    case 'failed':
      // A dropped connection, or an answer the page does not know. Either way
      // the order may not have gone, and pressing Place again is safe: the
      // same order goes under the same id, and a changed one is checked first.
      return 'The order did not go through. Your basket is kept - try again.';
  }
}

/** A changed basket after a failed Place, and My orders could not be read. */
const UNCHECKED =
  'We could not check whether your last try reached the nursery, so your ' +
  'changes were not sent. Your basket is kept - try again in a moment.';

type Choose = (item: ListedItem, typed: string) => void;

function QuantityBox({
  item,
  typed,
  problem,
  onChoose,
}: {
  readonly item: ListedItem;
  readonly typed: string;
  readonly problem: string | undefined;
  readonly onChoose: Choose;
}): React.JSX.Element {
  return (
    <label className="quantity">
      <span className="visually-hidden">How many {item.description}</span>
      <input
        className="input"
        inputMode="decimal"
        autoComplete="off"
        placeholder="0"
        aria-invalid={problem !== undefined}
        value={typed}
        onChange={(event) => {
          onChoose(item, event.target.value);
        }}
      />
    </label>
  );
}

const ItemRow = memo(function ItemRow({
  item,
  price,
  typed,
  problem,
  onChoose,
}: {
  readonly item: ListedItem;
  readonly price: Cents | undefined;
  readonly typed: string;
  readonly problem: string | undefined;
  readonly onChoose: Choose;
}): React.JSX.Element {
  return (
    <li className="item">
      <div className="item__text">
        <span className="item__name">{item.description}</span>
        <span className="fine faint">{detailOf(item)}</span>
      </div>
      <div className="item__figures">
        <span className="pill pill--good">In stock</span>
        {price === undefined ? (
          <span className="fine">Price on confirmation</span>
        ) : (
          <span>
            <span className="item__price">{money(price)}</span>{' '}
            <span className="fine faint">{perUnit(item)}</span>
          </span>
        )}
      </div>
      <QuantityBox item={item} typed={typed} problem={problem} onChoose={onChoose} />
      {problem === undefined ? null : (
        <span className="item__problem" role="status">
          {problem}
        </span>
      )}
    </li>
  );
});

export function OrderView({
  api,
  me,
  onPlaced,
}: {
  readonly api: PortalApi;
  readonly me: Me;
  /** The basket is done with: placed now, or placed by an earlier attempt. */
  readonly onPlaced: (settled: Settled) => void;
}): React.JSX.Element {
  const [availability, setAvailability] = useState<Availability | undefined>(undefined);
  const [unread, setUnread] = useState<string | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [basket, setBasket] = useState<Basket>(EMPTY_BASKET);
  // The id the next Place sends, and what a failed Place sent under it.
  const [ordering, setOrdering] = useState<Attempt>(() => ({ id: newOrderId() }));
  const [reviewing, setReviewing] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [addressId, setAddressId] = useState(
    () =>
      me.addresses.find((address) => address.isDefault)?.id ??
      me.addresses[0]?.id ??
      '',
  );
  const [requiredDate, setRequiredDate] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void api.availability().then((answer) => {
      if (!live) return;
      if (answer.ok) {
        setAvailability(answer.value);
        setUnread(undefined);
      } else {
        setUnread(describeRead(answer.error));
      }
    });
    return () => {
      live = false;
    };
  }, [api, attempt]);

  // The review starts at its top, not wherever the list was scrolled to.
  useEffect(() => {
    if (reviewing) window.scrollTo({ top: 0 });
  }, [reviewing]);

  const asAt = today();
  const catalogue = useMemo(
    () => (availability === undefined ? undefined : catalogueOf(availability, asAt)),
    [availability, asAt],
  );
  // A category that has since emptied is no filter, rather than an empty list.
  const category =
    catalogue?.categories.some((one) => one.id === categoryId) === true
      ? categoryId
      : '';
  const searched = useDeferredValue(query);
  const found = useMemo(
    () => (catalogue === undefined ? [] : matching(catalogue, searched, category)),
    [catalogue, searched, category],
  );
  const checked = useMemo(
    () => (catalogue === undefined ? undefined : checkBasket(basket, catalogue)),
    [basket, catalogue],
  );

  const choose = useCallback<Choose>((item, typed) => {
    setBasket((was) => withQuantity(was, item, typed));
  }, []);

  if (catalogue === undefined || checked === undefined) {
    return (
      <section className="stack">
        {unread === undefined ? (
          <p className="faint">Reading what is in stock…</p>
        ) : (
          <>
            <p className="notice notice--bad" role="alert">
              {unread}
            </p>
            <button
              className="button"
              type="button"
              onClick={() => {
                setUnread(undefined);
                setAttempt((was) => was + 1);
              }}
            >
              Try again
            </button>
          </>
        )}
      </section>
    );
  }

  const taxWords = catalogue.taxInclusive ? 'incl. VAT' : 'excl. VAT';
  const toFix = Object.keys(checked.problems).length;
  const byProduct = new Map<string, BasketLine>(
    checked.lines.map((line) => [line.item.productId, line]),
  );

  async function review(): Promise<void> {
    setProblem(undefined);
    setBasket(withoutBlanks);
    setReviewing(true);
    setRechecking(true);
    // Read the list again, so a plant that went while the basket was open is
    // marked here rather than discovered by the nursery.
    const answer = await api.availability();
    setRechecking(false);
    if (answer.ok) {
      setAvailability(answer.value);
    } else {
      setProblem(
        'We could not check what is in stock just now. You can still place the ' +
          'order: the nursery confirms every line.',
      );
    }
  }

  async function place(): Promise<void> {
    if (checked === undefined) return;
    const lines = checked.lines;
    setBusy(true);
    setProblem(undefined);
    const outcome = await placeOrder(
      api,
      ordering,
      (id) =>
        placeRequest(id, lines, {
          ...(addressId === '' ? {} : { deliveryAddressId: addressId }),
          requiredDate,
          note,
        }),
      newOrderId,
    );
    setBusy(false);
    switch (outcome.kind) {
      case 'failed':
        setOrdering(outcome.next);
        setProblem(describePlace(outcome.error));
        return;
      case 'unchecked':
        setProblem(UNCHECKED);
        return;
      case 'placed':
      case 'earlier':
        // An earlier order is the one that stands. The changed basket is not
        // kept: sent again whole it would be most of that order a second time,
        // and My orders says to place only what is still wanted.
        setBasket(EMPTY_BASKET);
        setOrdering({ id: newOrderId() });
        setReviewing(false);
        setRequiredDate('');
        setNote('');
        onPlaced(outcome);
    }
  }

  if (reviewing) {
    const entries = [...basket.values()];
    return (
      <section className="stack" aria-labelledby="review-title">
        <h2 id="review-title">Your order</h2>
        {rechecking ? (
          <p className="faint" role="status">
            Checking what is in stock…
          </p>
        ) : null}
        {problem === undefined ? null : (
          <p className="notice notice--bad" role="alert">
            {problem}
          </p>
        )}
        {entries.length === 0 ? (
          <p>Your basket is empty.</p>
        ) : (
          <ul className="reviews" aria-label="Plants in your order">
            {entries.map((entry) => {
              const productId = entry.item.productId;
              const item = catalogue.listed.get(productId) ?? entry.item;
              const line = byProduct.get(productId);
              const wrong = checked.problems[productId];
              return (
                <li key={productId} className="review">
                  <div className="review__text">
                    <span className="item__name">{item.description}</span>
                    <span className="fine faint">{detailOf(item)}</span>
                  </div>
                  <QuantityBox
                    item={item}
                    typed={entry.typed}
                    problem={wrong}
                    onChoose={choose}
                  />
                  <span className="review__figure">
                    {line === undefined
                      ? ''
                      : line.lineCents === undefined
                        ? 'On confirmation'
                        : money(line.lineCents)}
                  </span>
                  <button
                    className="button button--quiet review__remove"
                    type="button"
                    onClick={() => {
                      setBasket((was) => without(was, productId));
                    }}
                  >
                    Remove<span className="visually-hidden"> {item.description}</span>
                  </button>
                  {rechecking || catalogue.listed.has(productId) ? null : (
                    <span className="review__gone">{GONE}</span>
                  )}
                  {wrong === undefined ? null : (
                    <span className="item__problem">{wrong}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <p className="total">
          {money(checked.totalCents)} {taxWords}
          {checked.partlyPriced ? ', plus plants priced on confirmation' : ''}
        </p>

        {me.addresses.length === 0 ? null : (
          <label className="field">
            <span className="field__label">Deliver to</span>
            <select
              className="input"
              value={addressId}
              onChange={(event) => {
                setAddressId(event.target.value);
              }}
            >
              {me.addresses.map((address) => (
                <option key={address.id} value={address.id}>
                  {[address.line1, address.suburb, address.city]
                    .filter((part) => part !== undefined)
                    .join(', ')}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="field">
          <span className="field__label">
            Wanted by <span className="field__optional">optional</span>
          </span>
          <input
            className="input"
            type="date"
            min={asAt}
            value={requiredDate}
            onChange={(event) => {
              setRequiredDate(event.target.value);
            }}
          />
        </label>
        <label className="field">
          <span className="field__label">
            Note to the nursery <span className="field__optional">optional</span>
          </span>
          <textarea
            className="input"
            rows={3}
            maxLength={500}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>
        <p className="fine">
          The nursery confirms every order, decides how many of each plant it can
          supply, and prices it then. You will see it confirmed, with an order number,
          under My orders.
        </p>
        <button
          className="button button--primary"
          type="button"
          disabled={busy || rechecking || checked.lines.length === 0 || toFix > 0}
          onClick={() => {
            void place();
          }}
        >
          Place order
        </button>
        <button
          className="button"
          type="button"
          onClick={() => {
            setProblem(undefined);
            setReviewing(false);
          }}
        >
          Back to the list
        </button>
      </section>
    );
  }

  const filtered = searched.trim() !== '' || category !== '';
  const drawn = found.slice(0, shown);

  return (
    <section className="stack" aria-labelledby="list-title">
      <div className="stack stack--tight">
        <h2 id="list-title">In stock now</h2>
        <p className="fine faint">
          Prices are yours, {taxWords}. Order any quantity: the nursery confirms what it
          can supply.
        </p>
      </div>

      {catalogue.items.length === 0 ? (
        <p>Nothing is in stock to order just now. Check back soon, or call your rep.</p>
      ) : (
        <>
          <div className="filters" role="search">
            <label className="field">
              <span className="field__label">Search by name or code</span>
              <input
                className="input"
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setShown(PAGE);
                }}
              />
            </label>
            {catalogue.categories.length === 0 ? null : (
              <label className="field">
                <span className="field__label">Category</span>
                <select
                  className="input"
                  value={category}
                  onChange={(event) => {
                    setCategoryId(event.target.value);
                    setShown(PAGE);
                  }}
                >
                  <option value="">All categories</option>
                  {catalogue.categories.map((one) => (
                    <option key={one.id} value={one.id}>
                      {one.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <p className={found.length === 0 ? '' : 'fine faint'} aria-live="polite">
            {found.length === 0
              ? 'No plant in stock matches that. Try fewer letters, or another category.'
              : found.length > drawn.length
                ? `Showing the first ${String(drawn.length)} of ${plants(found.length)}. ` +
                  'Search or choose a category to narrow them.'
                : filtered
                  ? `${plants(found.length)} ${found.length === 1 ? 'matches' : 'match'}.`
                  : `${plants(found.length)} in stock.`}
          </p>

          <ul className="items" aria-label="Plants in stock">
            {drawn.map((item) => (
              <ItemRow
                key={item.productId}
                item={item}
                price={catalogue.prices.get(item.productId)}
                typed={basket.get(item.productId)?.typed ?? ''}
                problem={checked.problems[item.productId]}
                onChoose={choose}
              />
            ))}
          </ul>

          {found.length > drawn.length ? (
            <button
              className="button"
              type="button"
              onClick={() => {
                setShown((was) => was + PAGE);
              }}
            >
              Show {String(Math.min(PAGE, found.length - drawn.length))} more
            </button>
          ) : null}
        </>
      )}

      {/* The whole basket, whatever the search and category leave in view. */}
      <div className="basket" aria-live="polite">
        <span>
          {checked.chosen === 0
            ? 'Nothing chosen yet'
            : `${plants(checked.chosen)} · ${money(checked.totalCents)}${
                checked.partlyPriced ? ' + to confirm' : ''
              }${toFix === 0 ? '' : ` · ${String(toFix)} to fix`}`}
        </span>
        <button
          className="button button--primary"
          type="button"
          disabled={checked.chosen === 0}
          onClick={() => {
            // Opened with a problem still in it is fine: every line has its
            // box on the review, where one hidden by the search can be fixed.
            void review();
          }}
        >
          Review order
        </button>
      </div>
    </section>
  );
}
